import React, { useState } from 'react'
import FormInput from '../../Inputs/FormInput'
import styled from '@emotion/styled'
import { useMediaQueries } from 'shop/hooks'
import ToggleSwitchSection from 'shop/components/Forms/ToggleSwitchSection'
import { scrollToElement } from 'shop/components/Controls/utils'

interface Props {
  giftWrapMessage: string | null
  giftWrapPlaceholder?: string
  giftWrapPrice?: string
  giftWrapText?: string
  giftWrapped: boolean
  setGiftWrapMessage: (message: string | null) => void
  setGiftWrapped: (value: boolean) => void
  updateCartGiftWrapping: (message: string | null) => void
}

const GiftWrapping = ({
  giftWrapMessage,
  giftWrapPlaceholder,
  giftWrapPrice = '0.00',
  giftWrapText,
  giftWrapped,
  setGiftWrapMessage,
  setGiftWrapped,
  updateCartGiftWrapping
}: Props) => {
  const [isFocussed, setIsFocussed] = useState<boolean>(false)
  const [isTouched, setIsTouched] = useState<boolean>(false)
  const { isMobile } = useMediaQueries()

  // Ref to store the previous value of the gift wrap message sent to cart to prevent duplicate requests
  const inputValueRef = React.useRef<string | null>(
    giftWrapped ? giftWrapMessage : null
  )

  const isOverCharacterLimit =
    !!giftWrapMessage && giftWrapMessage?.length > 400

  const showError =
    (isTouched && !isFocussed && !giftWrapMessage?.length) ||
    isOverCharacterLimit

  const errorMessage = !giftWrapMessage?.length
    ? 'Please enter a message for gift wrapping'
    : 'You have exceeded the character limit for this input. Please limit your message to 400 characters'

  const giftWrapPriceNumeric: number = parseFloat(giftWrapPrice)
  const pricingDisplay: string =
    giftWrapPriceNumeric === 0
      ? 'For free'
      : `For only ${giftWrapPriceNumeric.toLocaleString('en-GB', {
          style: 'currency',
          currency: 'GBP'
        })}`

  /** Updates gift wrap message in consumer cart on blur if different to previous value sent */
  const handleBlur = () => {
    setIsTouched(true)
    setIsFocussed(false)
    if (giftWrapMessage !== inputValueRef.current) {
      inputValueRef.current = giftWrapMessage
      updateCartGiftWrapping(inputValueRef.current)
    }
  }

  /** Handles logic on user clicking add/remove button */
  const handleButtonClick = (isChecked: boolean) => {
    const nextGiftWrapped = isChecked

    if (!nextGiftWrapped && giftWrapMessage) {
      setGiftWrapMessage(null)
      inputValueRef.current = null
      updateCartGiftWrapping(null)
    }

    setGiftWrapped(nextGiftWrapped)
    setIsTouched(false)
    if (isMobile && nextGiftWrapped) {
      // scroll to section when toggling it on for Mobile
      // timeout for collapsiing section animations
      setTimeout(() => {
        scrollToElement('gift-wrap-section', 80)
      }, 400)
    }
  }

  const onGiftWrapInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTouched(true)
    setGiftWrapMessage(e.target.value)
  }

  const onGiftWrapInputFocus = () => setIsFocussed(true)

  return (
    <>
      <ToggleSwitchSection
        switchName='gift-wrap-switch'
        label={`${
          giftWrapText ? giftWrapText : 'Add a customised note'
        } (${pricingDisplay})`}
        onToggle={handleButtonClick}
        defaultChecked={giftWrapped}
      >
        <InputContainer>
          <FormInputContent>
            <FormInput
              variant='textarea'
              id={'giftWrapMessage'}
              data-testid='gift-wrap-message-textarea'
              value={giftWrapMessage || ''}
              onBlur={handleBlur}
              onFocus={onGiftWrapInputFocus}
              onChange={onGiftWrapInputChange}
              name='giftWrapMessage'
              labelText='Gift Wrap Message'
              errorMessage={errorMessage}
              showError={showError}
              placeholder={
                giftWrapPlaceholder ||
                'What would you like us to write on the note?'
              }
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'gift-wrap-section'
              }}
              tabIndex={!giftWrapped ? -1 : 0}
            />
          </FormInputContent>
        </InputContainer>
      </ToggleSwitchSection>
    </>
  )
}

const FormInputContent = styled.div(() => ({
  marginBottom: '24px',
  textarea: {
    minHeight: '80px'
  }
}))

const InputContainer = styled.div(() => ({
  paddingTop: '16px'
}))

export default GiftWrapping
